import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Input, Button, Textarea } from "@material-tailwind/react";
import axios from 'axios';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';
import { Alerty } from '../../common/Alerty';
import ReactSelect from 'react-select';

const PostOwnerTransaction = () => {
    const { user, token } = useAuth();
    const [owners, setOwners] = useState([]);
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [successOpen, setSuccessOpen] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [formData, setFormData] = useState({
        owner_code: '',
        trans_type: '',
        plate_no: '',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        description: '',
        reference_type: '',
        reference_id: '',
        payment_method: 'CASH',
        payment_reference: '',
        notes: ''
    });

    const referenceTypes = [
        { value: '', label: 'None' },
        { value: 'WEEKLY_REMITTANCE', label: 'Weekly Remittance' },
        { value: 'JOB_CARD', label: 'Job Card' },
        { value: 'EXPENSE', label: 'Expense' },
        { value: 'MANUAL', label: 'Manual' }
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchOwners();
        fetchTransactionTypes();
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!formData.owner_code) {
            setCars([]);
            setFormData(prev => ({ ...prev, plate_no: '' }));
            return;
        }
        fetchCars(formData.owner_code);
    }, [formData.owner_code]);

    const fetchOwners = async () => {
        try {
            const res = await axios.get(`${rootUrl}/owners?limit=1000`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOwners(res.data?.data ?? res.data ?? []);
        } catch (error) {
            console.error('Error fetching owners:', error);
            setErrorMessage('Failed to load owners');
            setErrorOpen(true);
        }
    };

    const fetchTransactionTypes = async () => {
        try {
            const res = await axios.get(`${rootUrl}/payments/owner-types`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const types = (res.data?.data || []).map(type => ({
                value: type.type,
                label: type.short_desc || type.type
            }));
            setTransactionTypes(types);
            // Set first type as default if available
            if (types.length > 0 && !formData.trans_type) {
                setFormData(prev => ({ ...prev, trans_type: types[0].value }));
            }
        } catch (error) {
            console.error('Error fetching transaction types:', error);
        }
    };

    const fetchCars = async (ownerCode) => {
        try {
            const res = await axios.get(`${rootUrl}/cars/plates?owner_code=${encodeURIComponent(ownerCode)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const payload = res.data || [];
            const plates = Array.isArray(payload) ? payload : [];
            setCars(plates);
        } catch (error) {
            console.error('Error fetching cars:', error);
            setCars([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.owner_code) newErrors.owner_code = 'Owner is required';
        if (!formData.trans_type) newErrors.trans_type = 'Transaction type is required';
        if (!formData.date) newErrors.date = 'Date is required';
        if (!formData.amount || isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Amount must be a positive number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                trans_type: formData.trans_type,
                plate_no: formData.plate_no || null,
                date: formData.date,
                amount: parseFloat(formData.amount),
                description: formData.description,
                reference_type: formData.reference_type || null,
                reference_id: formData.reference_id || null,
                payment_method: formData.payment_method,
                payment_reference: formData.payment_reference,
                notes: formData.notes,
                input_by: user?.username
            };

            const res = await axios.post(
                `${rootUrl}/api/owners/${formData.owner_code}/transactions`,
                payload,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (res.data.success) {
                setSuccessMessage(`Transaction posted successfully!`);
                setSuccessOpen(true);

                // Reset form
                setFormData({
                    owner_code: '',
                    trans_type: transactionTypes.length > 0 ? transactionTypes[0].value : '',
                    plate_no: '',
                    date: new Date().toISOString().split('T')[0],
                    amount: '',
                    description: '',
                    reference_type: '',
                    reference_id: '',
                    payment_method: 'CASH',
                    payment_reference: '',
                    notes: ''
                });
            } else {
                setErrorMessage(res.data.message || 'Failed to post transaction');
                setErrorOpen(true);
            }
        } catch (error) {
            console.error('Error posting transaction:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to post transaction');
            setErrorOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6 lg:p-8">
            <div className="max-w-3xl mx-auto">
                {/* Page Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Post Owner Transaction</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Record a new transaction for an owner
                    </p>
                </div>

                {successOpen && (
                    <div className="mb-6">
                        <Alerty
                            color="green"
                            message={successMessage}
                            setOpen={setSuccessOpen}
                        />
                    </div>
                )}

                {errorOpen && (
                    <div className="mb-6">
                        <Alerty
                            color="red"
                            message={errorMessage}
                            setOpen={setErrorOpen}
                        />
                    </div>
                )}

                {/* Form Card */}
                <Card className="bg-white rounded-2xl shadow-xl border-0">
                    <CardBody className="p-8">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Owner and Transaction Type */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Owner <span className="text-red-500">*</span>
                                    </label>
                                    <ReactSelect
                                        options={owners.map(owner => ({
                                            value: owner.code,
                                            label: `${owner.code} - ${owner.name}`
                                        }))}
                                        value={formData.owner_code ? owners
                                            .filter(o => o.code === formData.owner_code)
                                            .map(o => ({
                                                value: o.code,
                                                label: `${o.code} - ${o.name}`
                                            }))[0] : null}
                                        onChange={(option) => handleSelectChange('owner_code', option?.value || '')}
                                        isSearchable
                                        isClearable={false}
                                        placeholder="Select owner..."
                                        classNamePrefix="react-select"
                                    />
                                    {errors.owner_code && (
                                        <Typography color="red" variant="small" className="mt-1">
                                            {errors.owner_code}
                                        </Typography>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Transaction Type <span className="text-red-500">*</span>
                                    </label>
                                    <ReactSelect
                                        options={transactionTypes}
                                        value={transactionTypes.find(t => t.value === formData.trans_type)}
                                        onChange={(option) => handleSelectChange('trans_type', option?.value || '')}
                                        isSearchable
                                        isClearable={false}
                                        classNamePrefix="react-select"
                                    />
                                    {errors.trans_type && (
                                        <Typography color="red" variant="small" className="mt-1">
                                            {errors.trans_type}
                                        </Typography>
                                    )}
                                </div>
                            </div>

                            {/* Date and Amount */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Date <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                        error={!!errors.date}
                                    />
                                    {errors.date && (
                                        <Typography color="red" variant="small" className="mt-1">
                                            {errors.date}
                                        </Typography>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Amount <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        type="number"
                                        name="amount"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        placeholder="0.00"
                                        step="0.01"
                                        error={!!errors.amount}
                                    />
                                    {errors.amount && (
                                        <Typography color="red" variant="small" className="mt-1">
                                            {errors.amount}
                                        </Typography>
                                    )}
                                </div>
                            </div>

                            {/* Vehicle (Optional) */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Vehicle (Optional)
                                </label>
                                <ReactSelect
                                    options={[
                                        { value: '', label: 'None (Owner-level)' },
                                        ...cars.map(plate => ({
                                            value: plate,
                                            label: plate
                                        }))
                                    ]}
                                    value={formData.plate_no ? {
                                        value: formData.plate_no,
                                        label: formData.plate_no
                                    } : { value: '', label: 'None (Owner-level)' }}
                                    onChange={(option) => handleSelectChange('plate_no', option?.value || '')}
                                    isSearchable
                                    isClearable={false}
                                    classNamePrefix="react-select"
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Description
                                </label>
                                <Textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Enter transaction description..."
                                    rows={3}
                                />
                            </div>

                            {/* Reference Information */}
                            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                                <Typography variant="h6" color="blue-gray" className="font-semibold">
                                    Reference Information (Optional)
                                </Typography>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Reference Type
                                        </label>
                                        <ReactSelect
                                            options={referenceTypes}
                                            value={referenceTypes.find(t => t.value === formData.reference_type)}
                                            onChange={(option) => handleSelectChange('reference_type', option?.value || '')}
                                            isSearchable
                                            isClearable={false}
                                            classNamePrefix="react-select"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Reference ID
                                        </label>
                                        <Input
                                            type="text"
                                            name="reference_id"
                                            value={formData.reference_id}
                                            onChange={handleChange}
                                            placeholder="e.g., Job Card ID"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Payment Details */}
                            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                                <Typography variant="h6" color="blue-gray" className="font-semibold">
                                    Payment Details (Optional)
                                </Typography>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Payment Method
                                        </label>
                                        <ReactSelect
                                            options={[
                                                { value: 'CASH', label: 'Cash' },
                                                { value: 'CHEQUE', label: 'Cheque' },
                                                { value: 'MPESA', label: 'M-Pesa' },
                                                { value: 'BANK', label: 'Bank Transfer' },
                                                { value: 'OTHER', label: 'Other' }
                                            ]}
                                            value={{ value: formData.payment_method, label: formData.payment_method }}
                                            onChange={(option) => handleSelectChange('payment_method', option?.value || 'CASH')}
                                            isSearchable={false}
                                            isClearable={false}
                                            classNamePrefix="react-select"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Payment Reference
                                        </label>
                                        <Input
                                            type="text"
                                            name="payment_reference"
                                            value={formData.payment_reference}
                                            onChange={handleChange}
                                            placeholder="e.g., Cheque number, M-Pesa ref"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Notes
                                </label>
                                <Textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    placeholder="Additional notes about this transaction..."
                                    rows={3}
                                />
                            </div>

                            {/* Buttons */}
                            <div className="flex gap-4 pt-4">
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-6 py-3 shadow-none"
                                >
                                    {loading ? 'Posting...' : 'Post Transaction'}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => {
                                        setFormData({
                                            owner_code: '',
                                            trans_type: transactionTypes.length > 0 ? transactionTypes[0].value : '',
                                            plate_no: '',
                                            date: new Date().toISOString().split('T')[0],
                                            amount: '',
                                            description: '',
                                            reference_type: '',
                                            reference_id: '',
                                            payment_method: 'CASH',
                                            payment_reference: '',
                                            notes: ''
                                        });
                                        setErrors({});
                                    }}
                                    className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-sm px-6 py-3 shadow-none"
                                >
                                    Clear
                                </Button>
                            </div>
                        </form>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default PostOwnerTransaction;
